import axios from "axios";
import { useEffect, useState } from "react";
import InfiniteScroll from 'react-infinite-scroll-component';

// MUI
import {
    Close,
    Collections,
    NavigateBefore,
    NavigateNext,
    ZoomIn
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    Fade,
    IconButton,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import ImageListItemBar from '@mui/material/ImageListItemBar';

export default function GalleryPublic({ apipublic, gallery }) {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

    // State management
    const [allGalleryImages, setAllGalleryImages] = useState([]);
    const [displayedImages, setDisplayedImages] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [openLightbox, setOpenLightbox] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isZoomed, setIsZoomed] = useState(false);

    const IMAGES_PER_PAGE = 12;

    useEffect(() => {
        loadGallery();
    }, []);

    const loadGallery = async () => {
        try {
            const formData = new FormData();
            formData.append('get-gallery', '');
            const res = await axios.post(apipublic, formData);

            if (res.data.status === false) {
                setError(true);
                setLoading(false);
            } else {
                setAllGalleryImages(res.data.data);
                setDisplayedImages(res.data.data.slice(0, IMAGES_PER_PAGE));
                setHasMore(res.data.data.length > IMAGES_PER_PAGE);
                setLoading(false);
            }
        } catch (err) {
            setError(true);
            setLoading(false);
        }
    };

    const fetchMoreData = () => {
        if (displayedImages.length >= allGalleryImages.length) {
            setHasMore(false);
            return;
        }

        setTimeout(() => {
            const nextPage = currentPage + 1;
            const startIndex = currentPage * IMAGES_PER_PAGE;
            const endIndex = startIndex + IMAGES_PER_PAGE;
            const newImages = allGalleryImages.slice(startIndex, endIndex);

            setDisplayedImages([...displayedImages, ...newImages]);
            setCurrentPage(nextPage);
            setHasMore(endIndex < allGalleryImages.length);
        }, 500);
    };

    const handleImageClick = (image, index) => {
        setSelectedImage(image);
        setSelectedIndex(index);
        setOpenLightbox(true);
        setIsZoomed(false);
    };

    const handleClose = () => {
        setOpenLightbox(false);
        setSelectedImage(null);
        setIsZoomed(false);
    };

    const handlePrevious = () => {
        const newIndex = selectedIndex > 0 ? selectedIndex - 1 : displayedImages.length - 1;
        setSelectedIndex(newIndex);
        setSelectedImage(displayedImages[newIndex]);
        setIsZoomed(false);
    };

    const handleNext = () => {
        const newIndex = selectedIndex < displayedImages.length - 1 ? selectedIndex + 1 : 0;
        setSelectedIndex(newIndex);
        setSelectedImage(displayedImages[newIndex]);
        setIsZoomed(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowLeft') handlePrevious();
        else if (e.key === 'ArrowRight') handleNext();
        else if (e.key === 'Escape') handleClose();
    };

    const handleDownload = () => {
        if (selectedImage) {
            const link = document.createElement('a');
            link.href = `${gallery}${selectedImage.img}`;
            link.download = selectedImage.title || 'image';
            link.click();
        }
    };

    const handleShare = async () => {
        if (navigator.share && selectedImage) {
            try {
                await navigator.share({
                    title: selectedImage.title || 'Gallery Image',
                    text: 'Check out this image!',
                    url: window.location.href,
                });
            } catch (err) {
                console.log('Error sharing:', err);
            }
        }
    };

    const getColumnCount = () => {
        if (isSmallScreen) return 2;
        if (isMediumScreen) return 3;
        return 4;
    };

    return (
        <Box
            sx={{
                background: '#fff',
                minHeight: '100vh',
                pt: { xs: 12, md: 15 },
                pb: { xs: 10, md: 5 }
            }}
        >
            <Container maxWidth="xl">
                {/* Header Section */}
                <Fade in timeout={800}>
                    <Box textAlign="center" mb={5}>
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} mb={2}>
                            <Collections sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                            <Typography
                                variant="h3"
                                fontWeight={800}
                                sx={{
                                    color: theme.palette.text.primary,
                                    fontSize: { xs: '2rem', md: '2.5rem' }
                                }}
                            >
                                Our Gallery
                            </Typography>
                        </Stack>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ maxWidth: 600, mx: 'auto', mb: 2 }}
                        >
                            Explore our collection of memories and moments captured over time
                        </Typography>
                        {!loading && !error && (
                            <Chip
                                label={`${allGalleryImages.length} Photos`}
                                color="primary"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    px: 2,
                                    height: 32
                                }}
                            />
                        )}
                    </Box>
                </Fade>

                {/* Loading State */}
                {loading && (
                    <ImageList cols={getColumnCount()} gap={16} variant="masonry">
                        {[...Array(12)].map((_, index) => (
                            <ImageListItem key={index}>
                                <Skeleton
                                    variant="rectangular"
                                    height={200 + Math.random() * 200}
                                    sx={{ borderRadius: 2, bgcolor: 'grey.200' }}
                                />
                            </ImageListItem>
                        ))}
                    </ImageList>
                )}

                {/* Error State */}
                {error && (
                    <Fade in>
                        <Alert
                            severity="warning"
                            sx={{
                                mt: 5,
                                borderRadius: 2
                            }}
                        >
                            We are uploading images. Please wait to see them.
                        </Alert>
                    </Fade>
                )}

                {/* Gallery with Infinite Scroll */}
                {!loading && !error && (
                    <InfiniteScroll
                        dataLength={displayedImages.length}
                        next={fetchMoreData}
                        hasMore={hasMore}
                        loader={
                            <Box textAlign="center" py={4}>
                                <CircularProgress color="primary" />
                                <Typography color="text.secondary" mt={2}>Loading more images...</Typography>
                            </Box>
                        }
                        endMessage={
                            <Box textAlign="center" py={6}>
                                <Typography
                                    variant="h6"
                                    color="primary.main"
                                    fontWeight={600}
                                >
                                    ✨ You've reached the end! ✨
                                </Typography>
                                <Typography color="text.secondary" mt={1}>
                                    That's all our photos for now
                                </Typography>
                            </Box>
                        }
                    >
                        <ImageList
                            cols={getColumnCount()}
                            gap={16}
                            variant="masonry"
                        >
                            {displayedImages.map((item, index) => (
                                <Fade in timeout={500} key={item.img}>
                                    <ImageListItem
                                        onClick={() => handleImageClick(item, index)}
                                        sx={{
                                            cursor: 'pointer',
                                            overflow: 'hidden',
                                            borderRadius: 3,
                                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                            position: 'relative',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                                            '&:hover': {
                                                transform: 'translateY(-8px)',
                                                boxShadow: `0 12px 32px ${theme.palette.primary.main}25`,
                                                zIndex: 2,
                                                '& .image-overlay': {
                                                    opacity: 1,
                                                },
                                                '& img': {
                                                    transform: 'scale(1.1)',
                                                }
                                            }
                                        }}
                                    >
                                        <img
                                            srcSet={`${gallery + item.img}?w=400&fit=crop&auto=format&dpr=2 2x`}
                                            src={`${gallery + item.img}?w=400&fit=crop&auto=format`}
                                            alt={item.title || 'Gallery image'}
                                            loading="lazy"
                                            style={{
                                                borderRadius: 12,
                                                transition: 'transform 0.4s ease',
                                                display: 'block',
                                                width: '100%'
                                            }}
                                        />
                                        {/* Hover Overlay */}
                                        <Box
                                            className="image-overlay"
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.7) 100%)',
                                                opacity: 0,
                                                transition: 'opacity 0.4s ease',
                                                display: 'flex',
                                                alignItems: 'flex-end',
                                                justifyContent: 'center',
                                                padding: 2,
                                                borderRadius: 3
                                            }}
                                        >
                                            <ZoomIn sx={{ color: 'white', fontSize: 32 }} />
                                        </Box>
                                        {item.title && (
                                            <ImageListItemBar
                                                title={item.title}
                                                sx={{
                                                    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
                                                    borderRadius: '0 0 12px 12px',
                                                    '& .MuiImageListItemBar-title': {
                                                        fontSize: '0.85rem',
                                                        fontWeight: 500
                                                    }
                                                }}
                                            />
                                        )}
                                    </ImageListItem>
                                </Fade>
                            ))}
                        </ImageList>
                    </InfiniteScroll>
                )}
            </Container>

            {/* Enhanced Lightbox Dialog */}
            <Dialog
                open={openLightbox}
                onClose={handleClose}
                maxWidth={false}
                fullScreen
                onKeyDown={handleKeyDown}
                TransitionComponent={Fade}
                sx={{
                    '& .MuiDialog-paper': {
                        backgroundColor: 'rgba(0, 0, 0, 0.97)',
                        backdropFilter: 'blur(20px)'
                    }
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                    }}
                >
                    {/* Action Buttons - Top */}
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            position: 'absolute',
                            top: { xs: 10, md: 20 },
                            right: { xs: 10, md: 20 },
                            zIndex: 2
                        }}
                    >

                        <IconButton
                            onClick={handleClose}
                            sx={{
                                color: '#fff',
                                backgroundColor: 'rgba(211, 47, 47, 0.2)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(211, 47, 47, 0.3)',
                                '&:hover': {
                                    backgroundColor: 'rgba(211, 47, 47, 0.3)',
                                    transform: 'scale(1.1)',
                                    transition: 'all 0.3s ease'
                                }
                            }}
                        >
                            <Close fontSize="large" />
                        </IconButton>
                    </Stack>

                    {/* Navigation - Previous */}
                    {displayedImages.length > 1 && (
                        <IconButton
                            onClick={handlePrevious}
                            sx={{
                                position: 'absolute',
                                left: { xs: 10, md: 40 },
                                color: '#fff',
                                backgroundColor: 'rgba(2, 136, 209, 0.2)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(2, 136, 209, 0.3)',
                                zIndex: 2,
                                width: { xs: 40, md: 56 },
                                height: { xs: 40, md: 56 },
                                '&:hover': {
                                    backgroundColor: 'rgba(2, 136, 209, 0.3)',
                                    transform: 'scale(1.1)',
                                    transition: 'all 0.3s ease'
                                }
                            }}
                        >
                            <NavigateBefore fontSize="large" />
                        </IconButton>
                    )}

                    {/* Image Display */}
                    {selectedImage && (
                        <Box
                            onClick={() => setIsZoomed(!isZoomed)}
                            sx={{
                                maxWidth: isZoomed ? '100%' : '90%',
                                maxHeight: isZoomed ? '100%' : '90%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: isZoomed ? 'zoom-out' : 'zoom-in',
                                transition: 'all 0.3s ease',
                                position: 'relative'
                            }}
                        >
                            <img
                                src={`${gallery}${selectedImage.img}`}
                                alt={selectedImage.title || 'Gallery image'}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '90vh',
                                    objectFit: 'contain',
                                    borderRadius: isZoomed ? 0 : 12,
                                    transition: 'all 0.3s ease',
                                    boxShadow: isZoomed ? 'none' : '0 20px 60px rgba(0,0,0,0.5)'
                                }}
                            />
                        </Box>
                    )}

                    {/* Navigation - Next */}
                    {displayedImages.length > 1 && (
                        <IconButton
                            onClick={handleNext}
                            sx={{
                                position: 'absolute',
                                right: { xs: 10, md: 40 },
                                color: '#fff',
                                backgroundColor: 'rgba(2, 136, 209, 0.2)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(2, 136, 209, 0.3)',
                                zIndex: 2,
                                width: { xs: 40, md: 56 },
                                height: { xs: 40, md: 56 },
                                '&:hover': {
                                    backgroundColor: 'rgba(2, 136, 209, 0.3)',
                                    transform: 'scale(1.1)',
                                    transition: 'all 0.3s ease'
                                }
                            }}
                        >
                            <NavigateNext fontSize="large" />
                        </IconButton>
                    )}

                    {/* Image Counter & Title */}
                    {displayedImages.length > 1 && (
                        <Stack
                            spacing={1}
                            sx={{
                                position: 'absolute',
                                bottom: 30,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                alignItems: 'center',
                                zIndex: 2
                            }}
                        >
                            {selectedImage?.title && (
                                <Typography
                                    sx={{
                                        color: '#fff',
                                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                        backdropFilter: 'blur(10px)',
                                        padding: '6px 16px',
                                        borderRadius: '20px',
                                        fontSize: '0.9rem',
                                        fontWeight: 500
                                    }}
                                >
                                    {selectedImage.title}
                                </Typography>
                            )}
                            <Box
                                sx={{
                                    color: '#fff',
                                    backgroundColor: 'rgba(2, 136, 209, 0.3)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(2, 136, 209, 0.4)',
                                    padding: '8px 20px',
                                    borderRadius: '24px',
                                    fontFamily: 'monospace',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    letterSpacing: 1
                                }}
                            >
                                {selectedIndex + 1} / {displayedImages.length}
                            </Box>
                        </Stack>
                    )}
                </Box>
            </Dialog>
        </Box>
    );
}
